import type { EstadoOrdenServicio, OrdenServicio } from '@/lib/supabase/tipos-bd'

/**
 * La orden de servicio tal como la necesita la matriz: con los nombres ya
 * resueltos.
 *
 * La matriz muestra "Ferretería El Progreso · Cambios de acometida", no dos
 * uuid. Se traen en la misma consulta —no con una llamada por fila— porque
 * cien órdenes en pantalla serían trescientas consultas.
 */
export type OrdenServicioConRelaciones = OrdenServicio & {
  plan_modulo_id?: string | null
  proyecto_plan_id?: string | null
  radio_geocerca_m?: number | null
  plan_modulo?: {
    id: string
    codigo: string
    nombre: string
    avance: number
    horas: number
    elementos?: any[] | null
    alistamiento_estado?: string | null
    alistamiento_fecha?: string | null
    etapa?: {
      id: string
      nombre: string
      orden: number
      proyecto?: {
        id: string
        codigo: string
        nombre: string
        cliente?: string | null
        ubicacion?: string | null
        latitud?: number | null
        longitud?: number | null
        radio_geocerca_m?: number | null
      } | null
    } | null
    colaboradores?: {
      colaborador_id?: string
      avance_porcentaje?: number | null
      completado?: boolean | null
      horas_trabajadas?: number | null
      colaborador: { id: string; nombre: string } | null
    }[]
  } | null
  proyecto_plan?: {
    id: string
    codigo: string
    nombre: string
    cliente?: string | null
    ubicacion?: string | null
    latitud?: number | null
    longitud?: number | null
    radio_geocerca_m?: number | null
  } | null

  cliente: {
    id: string
    nombre: string
    documento: string
    /** De World Office cuando la app no la tenía (0271). */
    ciudad: string | null
    departamento: string | null
  } | null
  servicio: { id: string; nombre: string; codigo: string } | null
  zona: {
    id: string
    nombre: string
    ciudad: string | null
    departamento: string | null
  } | null
  /** Quién va a cotizarla, para poder enseñar el nombre y no el id. */
  cotiza_colaborador: { id: string; nombre: string } | null
  /** La cotización vigente, si ya se pidió. */
  cotizacion: {
    id: string
    numero_cotizacion: string | null
    estado: string
    valor_total: number | null
    /**
     * El asiento de World Office detrás de esta cotización.
     *
     * Null significa que NO hay documento real: es la provisional que dejó el
     * import del Excel, con número «PROV-…» y valor cero. Es lo que distingue
     * «todavía nadie le ha puesto precio» de «vale cero pesos», y es lo que
     * decide si la tarjeta ofrece enlazar.
     */
    wo_id_asiento: number | null
    /** El vendedor que se escogió al cotizar (tercero de World Office). */
    wo_id_vendedor?: number | null
    /** Lo que se proyectó de mano de obra. Es el punto de partida al programar. */
    horas_proyectadas: number | null
    /** Razón del rechazo, cuando el cliente dijo que no. */
    notas_decision: string | null
    /** La fecha del documento, para la hoja que se mira antes de aprobar. */
    fecha_cotizacion: string | null
    /**
     * La cabecera del documento en el espejo de World Office.
     *
     * Solo hace falta para dibujar la hoja: prefijo, vendedor y fecha son lo
     * que el papel lleva arriba y no están en nuestra tabla.
     */
    wo: {
      total: number | string | null
      prefijo: string | null
      vendedor_nombre: string | null
      fecha: string | null
    } | null
  } | null
  /** La orden de trabajo, si la cotización ya se aprobó. */
  orden_trabajo: {
    id: string
    numero: string
    estado: string
    colaborador_id: string | null
    /** Quién la tiene. En el tablero es la pregunta de media docena de columnas. */
    colaborador: { id: string; nombre: string } | null
    horas_asignadas: number | null
    horas_ejecutadas: number
    horas_extra: number
    fecha_inicio_programada: string | null
    fecha_fin_programada: string | null
    /** Compuerta 2: el colaborador pidió más tiempo y falta decidir. */
    solicita_mas_tiempo: boolean
    /** Pidió empezar sin poder probar que está en el sitio. */
    solicita_inicio_sin_ubicacion: boolean
    horas_adicionales_solicitadas: number | null
    justificacion_mas_tiempo: string | null
    notas_precierre: string | null
    /** Lo que el técnico escribió al terminar, tal cual lo dictó. */
    resumen_trabajo: string | null
    /** El mismo relato, redactado para el acta que firma el cliente. */
    resumen_acta: string | null
    acta_enviada_en: string | null
    acta_firmada_en: string | null
    /** Cuándo se entregó de verdad por WhatsApp (null = aún en cola de salida). */
    acta_wa_enviada_en: string | null
    /** Si el último envío del acta falló, por qué. */
    acta_envio_error: string | null
  } | null
}

/** Lo que el formulario de creación envía. */
export type NuevaOrdenServicio = {
  /**
   * El tercero de World Office que todavía no se ha podido adoptar.
   *
   * Solo lo lleva una orden levantada sin señal: al subirla se adopta el
   * cliente y este campo se cambia por su `cliente_id`. Nunca llega a la
   * base; vive solo en la cola del teléfono.
   */
  wo_id_tercero_pendiente?: number | null
  cliente_id: string
  servicio_id: string
  zona_id: string | null
  direccion: string
  descripcion: string | null
  fecha_solicitud: string
  notas: string | null
}

export type FiltrosMatriz = {
  busqueda: string
  estado: EstadoOrdenServicio | 'todos'
  servicioId: string | 'todos'
  zonaId: string | 'todos'
}

export const FILTROS_INICIALES: FiltrosMatriz = {
  busqueda: '',
  estado: 'todos',
  servicioId: 'todos',
  zonaId: 'todos',
}
